import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import heroOffice from "@/assets/hero-office.webp";
import heroOfficeMobile from "@/assets/hero-office-mobile.webp";

export function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Cinematic parallax on the background image
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative isolate min-h-[100svh] flex items-end overflow-hidden -mt-24 bg-[#0b0b0b]"
    >
      {/* Full-bleed parallax background image */}
      <motion.div
        aria-hidden="true"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-20 will-change-transform"
      >
        <picture>
          <source media="(max-width: 768px)" srcSet={heroOfficeMobile} />
          <img
            src={heroOffice}
            alt=""
            width={1920}
            height={1080}
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </picture>
      </motion.div>

      {/* Cinematic dark tint for legibility */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,15,0.55) 0%, rgba(10,10,15,0.35) 40%, rgba(10,10,15,0.65) 100%)",
        }}
      />

      {/* Subtle grid lines overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.10] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "180px 180px",
        }}
      />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 w-full pt-40 pb-20 lg:pt-48 lg:pb-28"
      >
        <div className="container mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-5xl">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.32em] text-accent mb-6"
            >
              Founder-First Strategic Execution Partner
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="font-display font-bold text-white tracking-[-0.035em] leading-[1.02] text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5.75rem] xl:text-[6.25rem] mb-12 max-w-[18ch] drop-shadow-[0_4px_30px_rgba(0,0,0,0.45)]"
            >
              Strategic tech &amp; AI advisory for founders.
              <span className="sr-only">
                Founder Mitra is a strategic technology and AI advisory partner for founder-led companies.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="hero-description text-white/75 text-lg md:text-xl lg:text-2xl leading-[1.55] max-w-3xl mb-14 font-light"
            >
              We help founder-led companies simplify decisions, architect scalable systems, redesign workflows, and execute with clarity across product, AI, and operations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Link
                to="/book-call"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-[hsl(248_62%_21%)] pl-3 pr-7 py-2.5 font-medium shadow-[0_22px_50px_-15px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-0.5"
              >
                <span className="grid place-items-center h-9 w-9 rounded-full bg-[hsl(248_62%_21%)] text-white transition-transform duration-300 group-hover:translate-x-0.5">
                  <ArrowRight className="h-4 w-4" />
                </span>
                Book a Strategic Consultation
              </Link>
              <Link
                to="/strategic-engagements"
                className="group inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/[0.06] backdrop-blur-md px-6 py-3 text-sm font-medium text-white hover:bg-white/15 transition-all duration-300"
              >
                Explore Engagements
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/ai-tech-readiness-audit"
                className="group inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/[0.06] backdrop-blur-md px-6 py-3 text-sm font-medium text-white hover:bg-white/15 transition-all duration-300"
              >
                Get AI &amp; Tech Readiness Audit
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/contact?topic=growth-challenge"
                className="group inline-flex items-center gap-2 text-sm font-medium text-white/85 hover:text-white transition-colors"
              >
                Discuss Your Growth Challenge
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
